/** GPU Emboss — fragment-shader port of `lib/filters/emboss.ts`.
 *
 *  Single-tap directional gradient with mid-grey bias. For each
 *  pixel: sample the neighbor in the chosen direction (one of the
 *  four diagonals), compute the signed RGB difference, scale by
 *  `k = amount/100`, and add 128. Output reads naturally as the
 *  classic "hammered metal" emboss effect.
 *
 *  Math runs in [0, 255] integer-domain to match the JS path's
 *  Uint8ClampedArray semantics — without scaling first, the float
 *  delta would be in [-1, 1] and `128/255 + k * delta` doesn't
 *  round-trip to the same bytes for boundary cases.
 *
 *  ── Y-axis convention ─────────────────────────────────────────
 *  Direction-sensitive: `dy = -1` on the CPU means "sample the
 *  neighbor one row UP in DOM space", but the runtime's natural
 *  `vec2 px = v_uv * u_dims - 0.5` puts px.y in bottom-up coords,
 *  flipping the offset's effective Y direction. Without
 *  compensation, NE/NW directions would sample SE/SW neighbors and
 *  the emboss would look like the light came from the opposite
 *  side. Fix: compute px in top-down coords and flip Y when
 *  forming srcUv. See twirl.ts for the broader rationale. */

import { applyIdentityCopy } from '../identity-copy';
import type { EmbossDirection, EmbossValue } from '../../filters/emboss';
import { applyShader } from '../runtime';

const EMBOSS_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform vec2 u_offset;   // (dx, dy) in source-canvas pixel space — one of (±1, ±1)
uniform float u_k;       // amount / 100
out vec4 outColor;

void main() {
  // px in CPU-aligned top-down coords (px.y = 0 at DOM top) so the
  // (dx, dy) offset has the same Y direction the CPU path uses.
  vec2 px = vec2(v_uv.x * u_dims.x, (1.0 - v_uv.y) * u_dims.y) - 0.5;
  vec2 neighborIdx = clamp(px + u_offset, vec2(0.0), u_dims - 1.0);
  vec4 src = texture(u_source, v_uv);
  // neighborIdx is in top-down coords; flip Y when forming the
  // sampling UV so the UNPACK_FLIP_Y source is read at the matching
  // DOM row.
  vec2 neighborUv = vec2(
    (neighborIdx.x + 0.5) / u_dims.x,
    1.0 - (neighborIdx.y + 0.5) / u_dims.y
  );
  vec4 neighbor = texture(u_source, neighborUv);
  // Work in [0, 255] domain to mirror the JS arithmetic exactly.
  vec3 delta = (src.rgb - neighbor.rgb) * 255.0;
  vec3 out255 = 128.0 + u_k * delta;
  vec3 rgb = clamp(floor(out255 + 0.5), 0.0, 255.0) / 255.0;
  outColor = vec4(rgb, src.a);
}
`;

function directionOffset(d: EmbossDirection): [number, number] {
  switch (d) {
    case 'NE':
      return [1, -1];
    case 'NW':
      return [-1, -1];
    case 'SE':
      return [1, 1];
    case 'SW':
      return [-1, 1];
  }
}

export function applyEmbossOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: EmbossValue,
): boolean {
  const amount = Math.max(0, value.amount);
  if (amount === 0) return applyIdentityCopy(source, target);
  const [dx, dy] = directionOffset(value.direction);
  return applyShader({
    source,
    target,
    fragShaderSrc: EMBOSS_FRAG,
    uniforms: {
      u_dims: { type: 'vec2', value: [source.width, source.height] },
      u_offset: { type: 'vec2', value: [dx, dy] },
      u_k: { type: 'float', value: amount / 100 },
    },
  });
}
